import { useParams, Link } from 'react-router-dom';
import { articleContents } from '@/mocks/about';
import { articles, categories } from '@/mocks/articles';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = articleContents[articleId as keyof typeof articleContents];
  const articleMeta = articles.find((a) => a.id === articleId);

  if (!article || !articleMeta) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回分类浏览
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== articleId)
    .slice(0, 3);

  const categoryName = categories.find((c) => c.id === article.category)?.name || '';

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen bg-background-50">
        <article>
          <section className="relative w-full h-[260px] md:h-[360px] flex items-center justify-center overflow-hidden">
            <img
              src={articleMeta.image}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60"></div>
            <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-500/80 text-white text-xs font-medium mb-4">
                {categoryName}
              </span>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center justify-center gap-4 text-white/60 text-xs">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>
          </section>

          <nav className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-8 relative z-10" aria-label="面包屑导航">
            <ol className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 bg-background-50 rounded-lg px-4 py-3 border border-background-200/70" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">首页</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li><span className="text-foreground-300">/</span></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/categories" className="hover:text-primary-600 transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">分类浏览</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li><span className="text-foreground-300">/</span></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to={`/categories?cat=${article.category}`} className="hover:text-primary-600 transition-colors cursor-pointer" itemProp="item">
                  <span itemProp="name">{categoryName}</span>
                </Link>
                <meta itemProp="position" content="3" />
              </li>
              <li><span className="text-foreground-300">/</span></li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-foreground-700 font-medium" itemProp="name">{article.title}</span>
                <meta itemProp="position" content="4" />
              </li>
            </ol>
          </nav>

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-3xl mx-auto">
              {article.tags && article.tags.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-8">
                  {article.tags.map((tag, idx) => (
                    <span key={idx} className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-lg md:prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-sm md:prose-p:text-base
                  prose-strong:text-foreground-900
                  [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:text-lg md:[&_h2]:text-xl [&_h2]:mt-10 [&_h2]:mb-4
                  [&_p]:text-foreground-600 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base
                "
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </section>

          {relatedArticles.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100/70">
              <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                <h3 className="font-heading font-bold text-foreground-900 text-lg md:text-xl mb-8 text-center">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
                  {relatedArticles.map((item) => (
                    <article key={item.id} className="group rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/70 transition-all duration-300 overflow-hidden cursor-pointer">
                      <Link to={`/article/${item.id}`} className="block">
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {item.title}
                          </h4>
                          <div className="flex items-center gap-2 text-foreground-400 text-xs">
                            <span>{item.date}</span>
                            <span>·</span>
                            <span>{item.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          author: { '@type': 'Organization', name: article.author },
          datePublished: article.date,
          description: articleMeta.excerpt,
          inLanguage: 'zh-CN',
          isAccessibleForFree: true,
          publisher: { '@type': 'Organization', name: 'JMTT', url: 'https://jmttsquad.github.io/' }
        }) }} />
      </main>
      <Footer />
    </>
  );
}